import React from "react";
import * as queryString from "query-string";
import { defaultFirstYear, indicators, maximumYear, minimumYear } from "../constants";
import { createSlug, getDefaultLastYear, toggleSetExistence } from "../utilities";
import {
    convertMaritalStatusToIsInUnion,
    filterPopulationData,
    filterResults,
    filterSurveyData,
    getPeriod,
    normalizeChartSets,
    normalizeDatabases,
    normalizeEmuData,
    normalizePopulationData,
    normalizeResults,
    normalizeRuns,
    normalizeSurveyData,
} from "../data";
import * as api from "../api/api";
import fileDownload from "js-file-download";
import { withErrorBoundary } from "../errors";
import { withTranslation } from "react-i18next";
import ViewRun from "../pages/ViewRun";

const createInitialState = () => ({
    loadingData: false,
    databases: undefined,
    runs: undefined,
    queryRunId: undefined,

    /* User drawer inputs */
    selectedExistingRunId: undefined,
    selectedExistingCountryNumericCode: undefined,
    selectedExistingRegion: undefined,

    selectedRunId: undefined,
    selectedCountryNumericCode: undefined,
    selectedRegion: undefined,
    period: {
        firstYear: defaultFirstYear,
        lastYear: getDefaultLastYear(),
    },

    /* View run */
    loadingRun: false,
    surveyData: undefined,
    populationData: undefined,
    emuData: undefined,
    results: undefined,

    /* Results */
    selectedResultsMeasure: "percentage",
    selectedResultsIndicator: indicators[0].value,
    selectedResultsMaritalStatus: "married",
    selectedExportedResultsMeasures: new Set(),
    selectedExportedResultsIndicators: new Set(),

    /* Charts */
    chartSets: [{ value: "complete", label: "Complete" }],
    selectedChartsMeasure: "percentage",
    selectedChartsMaritalStatus: "married",
    selectedChartsIndicators: new Set(
        indicators.filter((indicator) => indicator.chart).map((indicator) => indicator.value)
    ),
    showSurveyLegend: true,

    /* Export chart sets */
    selectedExportedChartSet: "complete",
    selectedExportedChartsMeasure: "percentage",
    selectedExportedChartsMaritalStatus: "married",
});

const ViewRunContainer = (props) => {
    const [state, setState] = React.useState(createInitialState());

    const { queryRunId } = state;

    const signal = api.getSignal();

    const setStateSafely = (newState) => {
        setState((prevState) => ({
            ...prevState,
            ...newState,
        }));
    };

    React.useEffect(() => {
        fetchRuns();

        return () => {
            signal.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (queryRunId) {
            viewRun();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryRunId]);

    const fetchRuns = () => {
        setStateSafely({
            loadingData: true,
        });

        // noinspection JSCheckFunctionSignatures
        Promise.all([
            api.fetchDatabases(signal.token),
            api.fetchRuns(false, false, signal.token),
            api.fetchChartSets(signal.token),
        ])
            .then((response) => {
                if (!response || response.some((item) => !item)) {
                    return;
                }

                const databases = normalizeDatabases(response[0].data);
                const runs = normalizeRuns(response[1].data);
                const chartSets = normalizeChartSets(response[2].data);

                let { run: selectedExistingRunId } = queryString.parse(props.location.search);

                if (selectedExistingRunId && !runs[selectedExistingRunId]) {
                    console.log(`Run ${selectedExistingRunId} does not exist`);

                    selectedExistingRunId = undefined;
                }

                const selectedExistingRegion =
                    (selectedExistingRunId &&
                        runs[selectedExistingRunId] &&
                        runs[selectedExistingRunId].regionCodes.length &&
                        runs[selectedExistingRunId].regionCodes[0]) ||
                    undefined;

                const selectedExistingCountryNumericCode =
                    (!selectedExistingRegion &&
                        selectedExistingRunId &&
                        runs[selectedExistingRunId] &&
                        runs[selectedExistingRunId].countryNumericCodes.length &&
                        runs[selectedExistingRunId].countryNumericCodes[0]) ||
                    undefined;

                setStateSafely({
                    databases: databases,
                    runs: runs,
                    chartSets: chartSets,
                    queryRunId: selectedExistingRunId,
                    selectedExistingRunId: selectedExistingRunId,
                    selectedExistingCountryNumericCode: selectedExistingCountryNumericCode,
                    selectedExistingRegion: selectedExistingRegion,
                });
            })
            .finally(() => {
                setStateSafely({
                    loadingData: false,
                });
            });
    };

    const getCountries = (runId) => {
        if (!runId) {
            return {};
        }

        const run = state.runs[runId];
        const divisions = state.databases[run.surveyDatabaseId].divisions;

        let countries = run.countryNumericCodes.reduce((result, countryNumericCode) => {
            if (divisions[countryNumericCode] && divisions[countryNumericCode].isCountry) {
                result[countryNumericCode] = {
                    numericCode: countryNumericCode,
                    ...divisions[countryNumericCode],
                };
            }

            return result;
        }, {});

        if (run.runType === "Subnational") {
            countries = {};
        }

        return countries;
    };

    const getRegions = (runId) => {
        if (!runId) {
            return [];
        }

        const run = state.runs[runId];

        const regions = run.regionCodes || [];
        return regions;
    };

    const selectDefaultCountryOrRegion = (runId) => {
        const countryNumericCodes = Object.keys(getCountries(runId));
        const regions = getRegions(runId);

        if (countryNumericCodes.length + regions.length === 1) {
            if (countryNumericCodes.length) {
                setStateSafely({
                    selectedExistingCountryNumericCode: parseInt(countryNumericCodes[0], 10),
                });
            } else {
                setStateSafely({
                    selectedExistingRegion: regions[0],
                });
            }
        }
    };

    const getSelectedCountryNumericCodeOrRegion = () => {
        return state.selectedRegion || state.selectedCountryNumericCode;
    };

    const createRunTitle = () => {
        let countryAlphaCodeOrRegion;

        if (state.selectedRegion) {
            countryAlphaCodeOrRegion = state.selectedRegion;
        } else if (state.selectedCountryNumericCode) {
            const selectedRun = state.runs[state.selectedRunId];

            countryAlphaCodeOrRegion =
                state.databases[selectedRun.surveyDatabaseId].divisions[state.selectedCountryNumericCode].alphaCode;
        }

        if (countryAlphaCodeOrRegion) {
            const selectedRun = state.runs[state.selectedRunId];

            const { firstYear, lastYear } = state.period;

            return props.t("Run {{name}} for {{countryAlphaCodeOrRegion}} during {{firstYear}}-{{lastYear}}", {
                name: selectedRun.name,
                countryAlphaCodeOrRegion: countryAlphaCodeOrRegion,
                firstYear: firstYear,
                lastYear: lastYear,
            });
        }
    };

    const viewRun = () => {
        setStateSafely({
            loadingRun: true,
        });

        const run = state.runs[state.selectedExistingRunId];
        // const runType = state.runs[state.selectedExistingRunId].runType;

        const countryNumericCodeOrRegion = state.selectedExistingRegion || state.selectedExistingCountryNumericCode;
        const selectedRegion = state.selectedExistingRegion || undefined;
        const selectedCountryNumericCode =
            (!state.selectedExistingRegion && state.selectedExistingCountryNumericCode) || undefined;

        // noinspection JSCheckFunctionSignatures
        Promise.all([
            api.fetchSurveyDatabase(
                run.surveyDatabaseId,
                countryNumericCodeOrRegion,
                state.selectedExistingRunId,
                undefined,
                signal.token
            ),
            api.fetchPopulationDatabase(run.populationDatabaseId, countryNumericCodeOrRegion, signal.token),
            api.fetchResults(
                state.selectedExistingRunId,
                undefined,
                countryNumericCodeOrRegion,
                undefined,
                signal.token
            ),
            run.emuDatabaseId
                ? api.fetchEmuDatabase(run.emuDatabaseId, countryNumericCodeOrRegion, signal.token)
                : new Promise((resolve) =>
                      resolve({
                          data: [],
                      })
                  ),
        ])
            .then((response) => {
                console.log("Fetched survey");

                if (!response || response.some((item) => !item)) {
                    return;
                }

                const [surveyData, populationData, results, emuData] = response;

                const period = getPeriod(results.data.proportions);

                setStateSafely({
                    period: period,
                    surveyData: normalizeSurveyData(surveyData.data),
                    populationData: normalizePopulationData(populationData.data),
                    emuData: normalizeEmuData(emuData.data),
                    results: {
                        proportions: normalizeResults(results.data.proportions),
                        populations: normalizeResults(results.data.populations),
                    },
                    selectedRunId: state.selectedExistingRunId,
                    selectedRegion: selectedRegion,
                    selectedCountryNumericCode: selectedCountryNumericCode,
                });
            })
            .finally(() => {
                setStateSafely({
                    loadingRun: false,
                });
            });
    };

    //------------------------------------------------------------------------------------------------------------------

    const onSelectRun = (runId) => {
        setStateSafely({
            selectedExistingRunId: runId,
            selectedExistingCountryNumericCode: undefined,
            selectedExistingRegion: undefined,
        });

        selectDefaultCountryOrRegion(runId);
    };

    const onSelectCountry = (countryNumericCode) => {
        setStateSafely({
            selectedExistingCountryNumericCode: countryNumericCode,
            selectedExistingRegion: undefined,
        });
    };

    const onSelectRegion = (region) => {
        setStateSafely({
            selectedExistingRegion: region,
        });
    };

    const onViewRun = () => {
        viewRun();
    };

    const onChangePeriod = (period) => {
        setStateSafely({
            period: period,
        });
    };

    // ------------------------------------------------   Results tab   ------------------------------------------------

    const onChangeResultsMeasure = (measure) => {
        setStateSafely({
            selectedResultsMeasure: measure,
            selectedResultsIndicator:
                measure === "percentage" || state.selectedResultsIndicator !== "ratioModernAny"
                    ? state.selectedResultsIndicator
                    : indicators[0].value,
        });
    };

    const onChangeResultsIndicator = (indicator) => {
        setStateSafely({
            selectedResultsIndicator: indicator,
        });
    };

    const onChangeResultsMaritalStatus = (martialStatus) => {
        setStateSafely({
            selectedResultsMaritalStatus: martialStatus,
        });
    };

    const onToggleExportedResultsMeasure = (measure) => {
        const selectedExportedResultsMeasures = new Set(state.selectedExportedResultsMeasures);

        toggleSetExistence(selectedExportedResultsMeasures, measure);

        setStateSafely({
            selectedExportedResultsMeasures: selectedExportedResultsMeasures,
        });
    };

    const onToggleExportedResultsIndicator = (indicator) => {
        const selectedExportedResultsIndicators = new Set(state.selectedExportedResultsIndicators);

        toggleSetExistence(selectedExportedResultsIndicators, indicator);

        setStateSafely({
            selectedExportedResultsIndicators: selectedExportedResultsIndicators,
        });
    };

    const onDownloadSelectedResults = () => {
        api.downloadResults(
            state.selectedRunId,
            undefined,
            getSelectedCountryNumericCodeOrRegion(),
            undefined,
            Array.from(state.selectedExportedResultsMeasures),
            Array.from(state.selectedExportedResultsIndicators),
            signal.token
        ).then((response) => {
            if (!response) {
                return;
            }

            const run = state.runs[state.selectedRunId];

            if (run) {
                const name = state.selectedRegion ? `${run.name}_${state.selectedRegion}` : run.name;
                fileDownload(response.data, createSlug(name, "results.csv"));
            }
        });
    };

    const onDownloadAllResults = () => {
        api.downloadResults(
            state.selectedRunId,
            undefined,
            getSelectedCountryNumericCodeOrRegion(),
            undefined,
            ["percentage", "count"],
            indicators.map((indicator) => indicator.value),
            signal.token
        ).then((response) => {
            if (!response) {
                return;
            }

            const run = state.runs[state.selectedRunId];

            if (run) {
                const name = state.selectedRegion ? `${run.name}_${state.selectedRegion}` : run.name;
                fileDownload(response.data, createSlug(name, "all_results.csv"));
            }
        });
    };

    const onDownloadAllData = () => {
        api.downloadData(state.selectedRunId, getSelectedCountryNumericCodeOrRegion(), signal.token).then(
            (response) => {
                if (!response) {
                    return;
                }

                const run = state.runs[state.selectedRunId];

                if (run) {
                    const name = state.selectedRegion ? `${run.name}_${state.selectedRegion}` : run.name;
                    fileDownload(response.data, createSlug(name, "all_data.zip"));
                }
            }
        );
    };

    const getMeasureResultsForPeriod = (measure) => {
        if (!state.results) {
            return;
        }

        const measureKey = measure === "percentage" ? "proportions" : "populations";

        return filterResults(state.results[measureKey], state.period.firstYear, state.period.lastYear);
    };

    const getTableResults = () => {
        const results = getMeasureResultsForPeriod(state.selectedResultsMeasure);

        if (!results) {
            return results;
        }

        const isInUnion = convertMaritalStatusToIsInUnion(state.selectedResultsMaritalStatus);

        return results[state.selectedResultsIndicator].filter((datum) => datum.isInUnion === isInUnion);
    };

    // -------------------------------------------------   Charts tab   ------------------------------------------------

    const onChangeChartsMeasure = (measure) => {
        setStateSafely({
            selectedChartsMeasure: measure,
        });
    };

    const onChangeChartsMaritalStatus = (martialStatus) => {
        setStateSafely({
            selectedChartsMaritalStatus: martialStatus,
        });
    };

    const onToggleChartIndicator = (chart) => {
        const selectedChartsIndicators = new Set(state.selectedChartsIndicators);

        toggleSetExistence(selectedChartsIndicators, chart);

        setStateSafely({
            selectedChartsIndicators: selectedChartsIndicators,
        });
    };

    const onChangeExportedChartSet = (chartSet) => {
        setStateSafely({
            selectedExportedChartSet: chartSet,
        });
    };

    const onChangeExportedChartsMeasure = (measure) => {
        setStateSafely({
            selectedExportedChartsMeasure: measure,
        });
    };

    const onChangeExportedChartsMaritalStatus = (maritalStatus) => {
        setStateSafely({
            selectedExportedChartsMaritalStatus: maritalStatus,
        });
    };

    const onDownloadChartSet = () => {
        api.downloadChartSet(
            state.selectedRunId,
            undefined,
            getSelectedCountryNumericCodeOrRegion(),
            undefined,
            state.selectedExportedChartSet,
            state.selectedExportedChartsMaritalStatus,
            state.selectedExportedChartsMeasure,
            signal.token
        ).then((response) => {
            if (!response) {
                return;
            }

            const run = state.runs[state.selectedRunId];

            if (run) {
                const prefix = `${run.name}_${state.selectedExportedChartSet}`;
                const name = state.selectedRegion ? `${prefix}_${state.selectedRegion}` : prefix;
                fileDownload(response.data, createSlug(name, "charts.pdf"));
            }
        });
    };

    const onDownloadSelectedCharts = () => {
        api.downloadCharts(
            state.selectedRunId,
            undefined,
            getSelectedCountryNumericCodeOrRegion(),
            undefined,
            state.selectedExportedChartsMaritalStatus,
            state.selectedExportedChartsMeasure,
            Array.from(state.selectedChartsIndicators),
            signal.token
        ).then((response) => {
            if (!response) {
                return;
            }

            const run = state.runs[state.selectedRunId];

            if (run) {
                const name = state.selectedRegion ? `${run.name}_${state.selectedRegion}` : run.name;
                fileDownload(response.data, createSlug(name, "charts.pdf"));
            }
        });
    };

    const onToggleSurveyLegend = () => {
        setStateSafely({
            showSurveyLegend: !state.showSurveyLegend,
        });
    };

    const getChartSurveyData = (surveyData, runType) => {
        if (!surveyData) {
            return;
        }

        const isInUnion = convertMaritalStatusToIsInUnion(state.selectedChartsMaritalStatus);

        return surveyData.filter((datum) => datum.isInUnion === isInUnion);
    };

    const getChartResults = () => {
        const measureResultsForPeriod = getMeasureResultsForPeriod(state.selectedChartsMeasure);

        if (!measureResultsForPeriod) {
            return;
        }

        const isInUnion = convertMaritalStatusToIsInUnion(state.selectedChartsMaritalStatus);

        const results = Object.keys(measureResultsForPeriod).reduce((result, indicator) => {
            if (state.selectedChartsIndicators.has(indicator)) {
                result[indicator] = measureResultsForPeriod[indicator].filter((datum) => datum.isInUnion === isInUnion);
            }

            return result;
        }, {});

        if (!Object.keys(results).length || !results[Object.keys(results)[0]].length) {
            return;
        }

        return results;
    };

    // ------------------------------------------------   Targets tab   ------------------------------------------------

    // Seperate container

    // -----------------------------------------------   Progress tab   ------------------------------------------------

    // Seperate container

    //------------------------------------------------------------------------------------------------------------------

    const { firstYear, lastYear } = {
        firstYear: minimumYear,
        lastYear: maximumYear,
    };

    const surveyData = filterSurveyData(state.surveyData, firstYear, lastYear);
    const populationData = filterPopulationData(state.populationData, firstYear, lastYear);

    const disableSelectedResultsDownload =
        !state.results &&
        !api.createResultsDownloadUrl(
            state.selectedRunId,
            undefined,
            getSelectedCountryNumericCodeOrRegion(),
            undefined,
            Array.from(state.selectedExportedResultsMeasures),
            Array.from(state.selectedExportedResultsIndicators)
        );

    const disableChartSetDownload =
        !state.results &&
        !api.createChartSetDownloadUrl(
            state.selectedRunId,
            undefined,
            getSelectedCountryNumericCodeOrRegion(),
            undefined,
            state.selectedExportedChartSet,
            state.selectedExportedChartsMaritalStatus,
            state.selectedExportedChartsMeasure
        );

    const disableSelectedChartsDownload =
        !state.results &&
        !api.createChartsDownloadUrl(
            state.selectedRunId,
            undefined,
            getSelectedCountryNumericCodeOrRegion(),
            undefined,
            state.selectedExportedChartsMaritalStatus,
            state.selectedExportedChartsMeasure,
            Array.from(state.selectedChartsIndicators)
        );

    const runType = state.runs?.[state.selectedRunId]?.runType;
    const countries = getCountries(state.selectedExistingRunId);
    const regions = getRegions(state.selectedExistingRunId);
    const runTitle = createRunTitle();
    const tableResults = getTableResults();
    const chartResults = getChartResults();
    const chartSurveyData = getChartSurveyData(surveyData, runType);
    const chartEmuData = state.selectedChartsMaritalStatus === "married" ? state.emuData : undefined;
    const disableAllDataDownload = !getSelectedCountryNumericCodeOrRegion();
    const disableAllResultsDownload = !getSelectedCountryNumericCodeOrRegion();

    return (
        <ViewRun
            match={props.match}
            loadingData={state.loadingData}
            runs={state.runs}
            countries={countries}
            regions={regions}
            selectedRunId={state.selectedRunId}
            selectedCountryNumericCode={state.selectedCountryNumericCode}
            selectedRegion={state.selectedRegion}
            selectedExistingRunId={state.selectedExistingRunId}
            selectedExistingCountryNumericCode={state.selectedExistingCountryNumericCode}
            selectedExistingRegion={state.selectedExistingRegion}
            period={state.period}
            loadingRun={state.loadingRun}
            runTitle={runTitle}
            surveyData={surveyData}
            populationData={populationData}
            emuData={state.emuData}
            selectedResultsMeasure={state.selectedResultsMeasure}
            selectedResultsIndicator={state.selectedResultsIndicator}
            selectedResultsMaritalStatus={state.selectedResultsMaritalStatus}
            tableResults={tableResults}
            selectedExportedResultsMeasures={Array.from(state.selectedExportedResultsMeasures)}
            selectedExportedResultsIndicators={Array.from(state.selectedExportedResultsIndicators)}
            disableSelectedResultsDownload={disableSelectedResultsDownload}
            disableAllResultsDownload={disableAllResultsDownload}
            disableAllDataDownload={disableAllDataDownload}
            selectedChartsMeasure={state.selectedChartsMeasure}
            selectedChartsMaritalStatus={state.selectedChartsMaritalStatus}
            selectedChartsIndicators={Array.from(state.selectedChartsIndicators)}
            showSurveyLegend={state.showSurveyLegend}
            chartResults={chartResults}
            chartSurveyData={chartSurveyData}
            chartEmuData={chartEmuData}
            chartSets={state.chartSets}
            selectedExportedChartSet={state.selectedExportedChartSet}
            selectedExportedChartsMeasure={state.selectedExportedChartsMeasure}
            selectedExportedChartsMaritalStatus={state.selectedExportedChartsMaritalStatus}
            disableChartSetDownload={disableChartSetDownload}
            disableSelectedChartsDownload={disableSelectedChartsDownload}
            onSelectRun={onSelectRun}
            onSelectCountry={onSelectCountry}
            onSelectRegion={onSelectRegion}
            onChangePeriod={onChangePeriod}
            onViewRun={onViewRun}
            onChangeResultsMeasure={onChangeResultsMeasure}
            onChangeResultsIndicator={onChangeResultsIndicator}
            onChangeResultsMaritalStatus={onChangeResultsMaritalStatus}
            onToggleExportedResultsMeasure={onToggleExportedResultsMeasure}
            onToggleExportedResultsIndicator={onToggleExportedResultsIndicator}
            onDownloadSelectedResults={onDownloadSelectedResults}
            onDownloadAllResults={onDownloadAllResults}
            onDownloadAllData={onDownloadAllData}
            onChangeChartsMeasure={onChangeChartsMeasure}
            onChangeChartsMaritalStatus={onChangeChartsMaritalStatus}
            onToggleChartIndicator={onToggleChartIndicator}
            onToggleSurveyLegend={onToggleSurveyLegend}
            onChangeExportedChartSet={onChangeExportedChartSet}
            onChangeExportedChartsMeasure={onChangeExportedChartsMeasure}
            onChangeExportedChartsMaritalStatus={onChangeExportedChartsMaritalStatus}
            onDownloadChartSet={onDownloadChartSet}
            onDownloadSelectedCharts={onDownloadSelectedCharts}
            runType={runType}
        />
    );
};

export default withTranslation()(withErrorBoundary(ViewRunContainer));
